import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-semibold ring-offset-background transition-all duration-250 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.97]",
  {
    variants: {
      variant: {
        /* Default - Coral vibrante com sombra definida */
        default: "bg-primary text-primary-foreground shadow-[0_2px_8px_-2px_hsl(var(--primary)/0.35)] hover:bg-primary/90 hover:shadow-[0_4px_16px_-4px_hsl(var(--primary)/0.4)] hover:-translate-y-[0.5px]",
        
        /* Destructive - Vermelho de alerta */
        destructive: "bg-destructive text-destructive-foreground shadow-[0_2px_8px_-2px_hsl(var(--destructive)/0.35)] hover:bg-destructive/90 hover:shadow-[0_4px_16px_-4px_hsl(var(--destructive)/0.4)]",
        
        /* Outline - Borda definida, fundo claro */
        outline: "border-[1.5px] border-border bg-background hover:bg-muted/60 hover:border-primary/40 hover:text-foreground",
        
        /* Secondary - Terracota */
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/85 hover:shadow-md",
        
        /* Ghost - Subtil mas visível */
        ghost: "hover:bg-muted/80 hover:text-foreground active:bg-muted",
        
        /* Link - Cor primária clara */
        link: "text-primary underline-offset-4 hover:underline font-medium hover:text-primary/80 active:text-primary/70",
        
        /* Premium - Gradiente Coral */
        premium: "gradient-primary text-white shadow-[0_4px_16px_-4px_hsl(var(--primary)/0.5)] hover:shadow-[0_6px_24px_-6px_hsl(var(--primary)/0.6)] hover:-translate-y-[1px] active:translate-y-0 active:shadow-[0_2px_8px_-2px_hsl(var(--primary)/0.4)]",
        
        /* Premium Outline */
        "premium-outline": "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary hover:shadow-[0_4px_12px_-4px_hsl(var(--primary)/0.3)] active:scale-[0.98]",
        
        /* Success - Verde confirmado */
        success: "bg-success text-success-foreground shadow-[0_2px_8px_-2px_hsl(var(--success)/0.3)] hover:bg-success/95 hover:shadow-[0_3px_12px_-3px_hsl(var(--success)/0.4)] active:translate-y-0",
      },
      size: {
        default: "h-10 px-5 py-2 rounded-xl",
        xs: "h-7 rounded-lg px-2.5 text-[10px]",
        sm: "h-8 rounded-lg px-3.5 text-xs",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-2xl px-10 text-lg",
        icon: "h-10 w-10 rounded-xl",
        "icon-sm": "h-8 w-8 rounded-lg",
        "icon-lg": "h-12 w-12 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };